import React, { useState } from "react";
import PropTypes from "kf-proptypes/widgets";
import classNames from "classnames";
import { Show } from "control-flow-react";

import {
  KfIcon,
  KF_ICONS,
  ICON_SIZE,
  ICON_STROKE
} from "@widgets/common/components/primitives/kf_icon/index.jsx";

import styles from "./banner.css";

export const BANNER_STATE = {
  WARNING: "warning",
  ERROR: "error",
  INFORMATION: "information",
  SUCCESS: "success"
};

export const BANNER_SIZE = {
  SMALL: "small",
  MEDIUM: "medium",
  LARGE: "large"
};

export const BANNER_TYPES = {
  TYPE1: "type1",
  TYPE2: "type2",
  TYPE3: "type3"
};

const ICON_MAPPING = {
  [BANNER_STATE.WARNING]: {
    icon: KF_ICONS.WARNING,
    color: "--kf-color-warning-500"
  },
  [BANNER_STATE.ERROR]: {
    icon: KF_ICONS.CLOSE_ROUND,
    color: "--kf-color-error-500"
  },
  [BANNER_STATE.INFORMATION]: {
    icon: KF_ICONS.INFO_WITH_CIRCLE,
    color: "--kf-color-gray-500"
  },
  [BANNER_STATE.SUCCESS]: {
    icon: KF_ICONS.SELECT_ROUND,
    color: "--kf-color-success-500"
  }
};

const TYPE_1 = BANNER_TYPES.TYPE1;
const TYPE_2 = BANNER_TYPES.TYPE2;
const TYPE_3 = BANNER_TYPES.TYPE3;
const SMALL_SIZE = BANNER_SIZE.SMALL;

/**
 * We can use this to show the information, warning , success and error messages
 * and also we can make it this closable
 */
export function Banner(props) {
  const {
    children,
    className = "",
    state = BANNER_STATE.WARNING,
    type = TYPE_1,
    size = BANNER_SIZE.LARGE,
    iconConfig = {},
    title,
    actionRenderer,
    closable = false,
    showIcon = true,
    onClose = () => {},
    afterClose = () => {}
  } = props;

  const [isOpen, setIsOpen] = useState(true);

  const [isBannerVisible, setIsBannerVisible] = useState(true);

  const bannerWrapperClassName = classNames(
    styles.bannerWrapper,
    styles[type],
    {
      [styles.bannerWrapperVisible]: isOpen
    }
  );
  const bannerClassName = classNames(styles.banner, styles[state], className, {
    [styles[size]]: type === TYPE_1,
    [styles.bannerWithClosable]: closable
  });

  const messageClassName = classNames(
    {
      small: type === TYPE_1 && size === SMALL_SIZE,
      base: type === TYPE_3 || (type === TYPE_1 && size !== SMALL_SIZE),
      small400: type === TYPE_2
    },
    styles.message
  );
  const titleClassName = classNames("base600", styles.title);

  const actionRendererClassName = classNames({
    [styles.actionRenderer]: actionRenderer
  });

  const { icon: iconName, stroke: iconStroke, coloured = false } = iconConfig;

  const bannerIcon = iconName || ICON_MAPPING[state].icon;
  const iconSize =
    TYPE_1 === type && SMALL_SIZE === size
      ? ICON_SIZE.SIZE_16
      : ICON_SIZE.SIZE_20;

  const Message = <div className={messageClassName}>{children}</div>;

  const Title = <div className={titleClassName}>{title}</div>;

  const ActionRenderer = (
    <div className={actionRendererClassName}>{actionRenderer}</div>
  );

  const BANNER_CONTENT = {
    [TYPE_1]: [Message],
    [TYPE_2]: [Title, Message, ActionRenderer],
    [TYPE_3]: [Message]
  };

  return (
    <Show when={isBannerVisible}>
      <div
        className={bannerWrapperClassName}
        onTransitionEnd={(e) => {
          if (e.propertyName === "grid-template-rows") {
            afterClose();
            setIsBannerVisible(false);
          }
        }}
      >
        <div className={styles.bannerContainer}>
          <div
            className={bannerClassName}
            data-component="bannernotification"
            aria-label={`${state} banner`}
          >
            {showIcon && (
              <KfIcon
                icon={bannerIcon}
                size={iconSize}
                stroke={coloured ? undefined : iconStroke || ICON_STROKE.BOLD}
                color={!coloured && ICON_MAPPING[state].color}
                className={styles.icon}
              />
            )}
            <span className={styles.text}>{BANNER_CONTENT[type]}</span>
            <Show when={closable}>
              <KfIcon
                icon={KF_ICONS.CLOSE}
                size={ICON_SIZE.SIZE_12}
                stroke={ICON_STROKE.REGULAR}
                className={styles.closeIcon}
                onClick={() => {
                  onClose();
                  setIsOpen(false);
                }}
              />
            </Show>
          </div>
        </div>
      </div>
    </Show>
  );
}

Banner.propTypes = {
  /** Banner content	 */
  children: PropTypes.node.isRequired,
  /** Types of Banner	 */
  type: PropTypes.oneOf(Object.values(BANNER_TYPES)),
  /** title of the Banner. This is applicable only for type2 */
  title: PropTypes.string,
  /** custom icon for banner. Give only Kf-icons	 */
  iconConfig: PropTypes.shape({
    icon: PropTypes.string.isRequired,
    stroke: PropTypes.string,
    coloured: PropTypes.bool
  }),
  /**  State of Banner	 */
  state: PropTypes.oneOf(Object.values(BANNER_STATE)),
  /**  Size of Banner. This is applicable only for type1 */
  size: PropTypes.oneOf(Object.values(BANNER_SIZE)),
  /**  */
  className: PropTypes.string,
  /** actionRenderer for type2 banner 	 */
  actionRenderer: PropTypes.node,
  /** Banner Closable behaviour	 */
  closable: PropTypes.bool,
  /** callback will call when closed icon clicked. This is applicable only if "closable" is given 	 */
  onClose: PropTypes.func,
  /** callback will call after banner is closed. This is applicable only if "closable" is given */
  afterClose: PropTypes.func,
  /** A property to hide banner icon */
  showIcon: PropTypes.bool
};
